/**
 * Modal for picking a language to translate a risk analysis document into.
 *
 * Single-select only (maxItems=1) for now — matches the one call site this is
 * used from. The maxItems>1 branch is kept for prop-parity with the RN version
 * of this modal but isn't exercised anywhere in this app yet.
 *
 * @author Robbe De Geyndt <robbe.degeyndt@aware.be>
 */

'use strict';

//imports
import React from 'react';
import {Modal, Button} from 'react-bootstrap';
import CustomInput from '../../Components/CustomInput';
import CustomCheckBox from '../../Components/CustomCheckBox';
import CustomTranslation from '../../Components/CustomTranslation';

//define class
class RequestTranslationModal extends React.Component {

    //constructor
    constructor(props) {
        super(props);

        //state
        this.state = {
            search: '',
            selected: []
        }
    }

    //get the filtered/sorted language options
    getFilteredOptions = () => {
        const excluded = this.props.excludeLanguageCodes || [];

        return (this.props.languages || [])
            .filter((lang) => !excluded.includes(lang.languageCode))
            .map((lang) => ({code: lang.languageCode, name: lang.nativeName || lang.name}))
            .filter((lang) => lang.name.toLowerCase().includes(this.state.search.toLowerCase()))
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    //toggle a language's selected state
    toggle = (code, checked) => {
        const maxItems = this.props.maxItems || 1;

        if (maxItems === 1) {
            this.setState({selected: checked ? [code] : []});
            return;
        }

        this.setState((prevState) => {
            if (checked) {
                if (prevState.selected.includes(code) || prevState.selected.length >= maxItems) return prevState;
                return {selected: [...prevState.selected, code]};
            }
            return {selected: prevState.selected.filter((c) => c !== code)};
        });
    }

    //reset and close
    close = () => {
        this.setState({search: '', selected: []});
        this.props.onClose();
    }

    //submit the selection
    submit = () => {
        if (this.state.selected.length === 0) return;
        let codes = this.state.selected;
        this.setState({search: '', selected: []});
        this.props.onSubmit(codes);
    }

    //render view
    render = () => {

        let options = this.getFilteredOptions();

        return (
            <Modal show={this.props.visible} onHide={this.close} backdrop={'static'} keyboard={false}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {this.props.title || <CustomTranslation value={'KEY.Request translation'} />}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.props.description &&
                        <p className={'vertical-margin-2'}>{this.props.description}</p>
                    }
                    <div className={'vertical-margin-2'}>
                        <CustomInput
                            noLabel
                            inputProps={{placeholder: 'Search...'}}
                            onChangeText={(text) => this.setState({search: text})}
                        />
                    </div>
                    <div style={{maxHeight: '16rem', overflowY: 'auto'}}>
                        {options.map((lang) => {
                            const isSelected = this.state.selected.includes(lang.code);
                            return (
                                <div key={lang.code} className={'vertical-padding-3'}>
                                    <CustomCheckBox
                                        key={lang.code + '-' + (isSelected ? 'on' : 'off')}
                                        label={lang.name}
                                        value={isSelected}
                                        onValueChange={(checked) => this.toggle(lang.code, checked)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div style={{display: 'flex', flexDirection: 'row', width: '100%'}}>
                        <div className={'standard-margin-3'} style={{flex: 1, marginLeft: 0}}>
                            <Button block variant={'dark'} onClick={this.close}>
                                <CustomTranslation value={'KEY.Cancel'} />
                            </Button>
                        </div>
                        <div className={'standard-margin-3'} style={{flex: 1, marginRight: 0}}>
                            <Button
                                block
                                variant={'success'}
                                disabled={this.state.selected.length === 0}
                                onClick={this.submit}
                            >
                                {this.props.submitLabel || <CustomTranslation value={'KEY.Request translation'} />}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

//export
export default RequestTranslationModal;
